"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

interface IFrameWithFullscreenProps {
  src: string;
  title: string;
  width?: string;
  height?: string | number;
  style?: CSSProperties;
  allow?: string;
  allowFullScreen?: boolean;
  loading?: "lazy" | "eager";
}

export default function IFrameWithFullscreen({
  src,
  title,
  width,
  height,
  style,
  allow,
  allowFullScreen,
  loading,
}: IFrameWithFullscreenProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const syncIframeDocumentLayout = (iframe: HTMLIFrameElement) => {
    try {
      const iframeDocument = iframe.contentDocument;
      const iframeHtml = iframeDocument?.documentElement;
      const iframeBody = iframeDocument?.body;

      if (!iframeHtml || !iframeBody) {
        return;
      }

      iframeHtml.style.width = "100%";
      iframeHtml.style.height = "100%";
      iframeHtml.style.margin = "0";
      iframeHtml.style.overflow = "hidden";
      iframeHtml.style.scrollbarGutter = "auto";

      iframeBody.style.width = "100%";
      iframeBody.style.height = "100%";
      iframeBody.style.margin = "0";
      iframeBody.style.overflow = "hidden";
    } catch {
      // Ignore cross-origin iframes that block document access.
    }
  };

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    const originalScrollbarGutter = document.documentElement.style.scrollbarGutter;
    document.body.style.overflow = "hidden";
    document.documentElement.style.scrollbarGutter = "auto";

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.scrollbarGutter = originalScrollbarGutter;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  return (
    <div
      style={{
        position: isFullscreen ? "fixed" : "relative",
        ...style,
        ...(isFullscreen && {
          inset: 0,
          backgroundColor: "#000",
          margin: 0,
          overflow: "hidden",
          zIndex: 1000,
        }),
      }}
    >
      <iframe
        src={src}
        title={title}
        width={width}
        style={{
          position: isFullscreen ? "absolute" : "static",
          inset: isFullscreen ? 0 : undefined,
          width: "100%",
          height:
            isFullscreen
              ? "100%"
              : typeof height === "number"
                ? `${height}px`
                : height,
          border: "none",
          borderRadius: isFullscreen ? "0" : "12px",
          display: "block",
        }}
        allow={allow}
        allowFullScreen={allowFullScreen}
        loading={loading}
        onLoad={(event) => {
          syncIframeDocumentLayout(event.currentTarget);
        }}
      />
      {allowFullScreen&& (
         <button
          onClick={() => setIsFullscreen((current) => !current)}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: "rgba(0, 0, 0, 0.6)",
            border: "none",
            borderRadius: "6px",
            padding: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(0, 0, 0, 0.8)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(0, 0, 0, 0.6)";
          }}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <Minimize2 size={20} color="white" />
          ) : (
            <Maximize2 size={20} color="white" />
          )}
        </button>
      )}

    </div>
  );
}
